/** @format */

import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Tabs, Tab, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { Button, Chip, Grid } from '@mui/material';
import clsx from 'clsx';
import { Icons } from 'components';

const StepperKeeper = ({
  titles,
  panels,
  checkDeactive,
  tabsErrors,
  formAction,
  onStepChanged = null,
  forceSelectedTabIndex = null,
  stepsToHideNext = [], // 0,1,2,...,steps-1
  stepsToHidePrevious = [], // 0,1,2,...,steps-1 //TODO: Reduce Opacity of unreachable indexes to inform user
  unreachableIndexes = [],
  onChangeTab,
  checkNextStepAllowed,
  ...rest
}) => {
  const classes = useStyles();
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const titlesLng = titles.length;

  const tabIndex = forceSelectedTabIndex != null ? forceSelectedTabIndex : activeTabIndex;

  const setTabIndex = onStepChanged ?? setActiveTabIndex;

  const handleSelect = async (targetIndex) => {
    if (unreachableIndexes?.includes(targetIndex)) return;
    if (targetIndex > tabIndex && stepsToHideNext?.includes(tabIndex)) return;
    if (targetIndex < tabIndex && stepsToHidePrevious?.includes(tabIndex)) return;
    if (onChangeTab) {
      onChangeTab(targetIndex);
    }
    if (typeof checkNextStepAllowed === 'function') {
      const valid = await checkNextStepAllowed(tabIndex);
      if (!valid) {
        return;
      }
    }

    if (unreachableIndexes?.includes[targetIndex]) return;
    if (targetIndex > tabIndex && stepsToHideNext?.includes(tabIndex)) return;
    if (targetIndex < tabIndex && stepsToHidePrevious?.includes(tabIndex)) return;
    if (checkDeactive) {
      if (stepsToHideNext[0] >= targetIndex) {
        setTabIndex(targetIndex);
      } else {
        return
      }
    }

    setTabIndex(targetIndex);
  };

  return (
    <Tabs
      className={classes.root}
      forceRenderTabPanel
      {...rest}
      selectedIndex={tabIndex}
      onSelect={(index) => handleSelect(index)}
    >
      <TabList>
        {titles.map((tab, index) => (
          <Tab key={index}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Chip
                className={clsx(classes.chip, {
                  chipError: tabsErrors && tabsErrors?.[index],
                  disabled: stepsToHideNext < index,
                  current: stepsToHideNext === index,
                })}
                label={index + 1}
              />
              <span
                className={clsx(classes.stepTitle, {
                  hasError: tabsErrors && tabsErrors?.[index],
                  disabledText: stepsToHideNext < index,
                })}
              >
                {tab.label}
              </span>
              {/* {tabsErrors && tabsErrors?.[index] ? (
                <div className={classes.errors}>{tabsErrors[index]}</div>
              ) : null} */}
              {index + 1 !== titlesLng && (
                <div
                  className={clsx({
                    [classes.line]: true,
                    [classes.dashedLine]: tab?.liner,
                  })}
                />
              )}
            </div>
          </Tab>
        ))}
      </TabList>
      <div className={classes.panelsContainer}>
        {panels.map((v, k) => (
          <TabPanel key={k}>{v}</TabPanel>
        ))}
      </div>

      <Grid container item xs={12} className={classes.actionContainer} justifyContent="space-between">
        <Grid item >
          {tabIndex === 0 && !stepsToHideNext?.includes(tabIndex) ? (
            <Button
              variant="outlined"
              className={classes.stepperAction}
              startIcon={<Icons icon="CaretLeft" color="#2b3990" size="25" />}
              onClick={() => handleSelect(tabIndex + 1)}
            >
              {titles[1].label}
            </Button>
          ) : null}
          {tabIndex > 0 && (
            <>
              {!stepsToHidePrevious?.includes(tabIndex) && (
                <Button
                  variant="outlined"
                  className={classes.stepperAction}
                  startIcon={<Icons icon="CaretRight" color="#2b3990" size="25" />}
                  onClick={() => handleSelect(tabIndex - 1)}
                >
                  {titles[tabIndex - 1].label}
                </Button>
              )}
              {tabIndex < titlesLng - 1 && !stepsToHideNext?.includes(tabIndex) && (
                <Button
                  variant="outlined"
                  className={classes.stepperAction}
                  startIcon={<Icons icon="CaretLeft" color="#2b3990" size="25" />}
                  style={{ marginRight: '25px' }}

                  onClick={() => handleSelect(tabIndex + 1)}
                >
                  {titles[tabIndex + 1].label}
                </Button>
              )}
            </>
          )}
        </Grid>

        {formAction && (
          <Grid item >
            {formAction}
          </Grid>
        )}
      </Grid>
    </Tabs>
  );
};

const useStyles = makeStyles((theme) => {
  return {
    line: {
      width: '90px',
      border: '1px solid #A1AAE380',
      marginLeft: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        width: '20px',
      },
    },
    dashedLine: {
      border: '1px solid #A1AAE380',
    },
    chip: {
      borderRadius: '4px',
      marginRight: '8px',
      fontSize: '13px',
      background: ' #A1AAE312',
      width: 32,
      border: '1px solid #A1AAE380',
      '& .MuiChip-label': {
        textOverflow: 'inherit',
      },

      '&.chipError': {
        color: '#e53935 !important',
        background: 'rgb(207,57,23,.26) !important',
      },
    },
    stepTitle: {
      color: '#303d8f !important',
      '&.hasError': {
        color: '#e53935 !important',
      },
    },
    root: {
      width: '100%',
      '& *, *:focus': {
        boxShadow: 'none !important',
      },
      '& .react-tabs__tab-list': {
        borderBottom: 0,
        display: 'flex',
        overflowX: 'auto',
        overflowY: 'hidden',
        alignItems: 'center',
        '&::-webkit-scrollbar': {
          width: 2,
          height: 4,
          borderRadius: 10,
        },

        /* Track */
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: 10,
        },

        /* Handle */
        '&::-webkit-scrollbar-thumb': {
          background: '#e0e0e0',
          borderRadius: 10,
        },

        /* Handle on hover */
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#bdbbbb',
          borderRadius: 10,
        },
      },
      '& .react-tabs__tab': {
        border: 'none',
        color: '#546e7a',
        boxShadow: 'none',
        borderRadius: 0,
        outline: 'none',
        fontWeight: 500,
        fontSize: 14,
        transition: 'ease border .25s',
        borderBottom: '2px solid transparent',
        '&--selected': {
          color: theme.palette.primary.main,
          borderBottom: 0,
          '& .MuiChip-root': {
            color: '#ffffff',
            background: theme.palette.primary.main,
          },
        },
        '&:focus:after': {
          display: 'none',
          borderBottom: `2px solid ${theme.palette.primary.main}`,
        },
      },
    },
    panelsContainer: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
    actionContainer: {
      paddingTop: theme.spacing(2),
      justifyContent: 'flex-end',
      flexDirection: ' row',
      alignItems: 'center',
      gap: '16px',
      display: 'flex',
    },
    stepperAction: { color: theme.palette.primary.main },
    errors: {
      background: 'rgba(100,0,0,.15)',
      color: 'red',
      height: 22,
      paddingInlineStart: theme.spacing(1),
      paddingInlineEnd: theme.spacing(1),
      paddingTop: theme.spacing(0.3),
      display: 'inline-block',
      borderRadius: 100,
      marginInlineStart: theme.spacing(1.4),
    },
  };
});

export default StepperKeeper;
